// apps/client/src/world/spaces/museum.ts (M2-T10) — the museum interior (§8.4, §9.8): the toy-room shell from world/interior.ts, the
// fossil hall (wall plaques for single fossils; a skeleton standing on a plinth for each multi-part set, assembling as parts arrive, a ghost bone
// for every part still missing), the river/pond/sea + feature tanks with donated fish swimming, and Professor Reed the heron behind
// his lectern. Layout tiles come from tuning.spaces.museum (x → X, y → Z). Animation runs through SpaceScene.update (rev 5 item 12).
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { getItem, tuning, type CatalogId, type FishEntry, type FossilEntry, type PatternSpec } from '@whiskerton/content';
import type { MuseumState, SpaceSnapshot, Vec2 } from '@whiskerton/shared';
import { fossilSets, museumTank } from '@whiskerton/shared/rules/museum';
import { bakeFish, fishScale } from '../../assets/builders/fish';
import { skeletonSlot } from '../../assets/builders/fossil';
import type { AssetService } from '../../assets/service';
import { toyMaterial } from '../../characters/doll';
import { createCurator } from '../../characters/curator';
import type { Game } from '../../game/game';
import type { SpaceScene, SpaceSceneDef } from '../../game/spaces';
import { bakeMeshes } from '../bake';
import { interiorScene } from '../interior';

export interface DisplaySlot { set: string; parts: CatalogId[]; x: number; y: number; w: number; mount: 'floor' | 'wallBack' | 'wallLeft' }
// The museum's own surfaces: warm gallery stone (art constants, like any builder's colours).
const WALL: PatternSpec = { kind: 'stripes', base: '#EFE6D2', accent: '#E6DBC2', scale: 1.5 };
const FLOOR: PatternSpec = { kind: 'herringbone', base: '#C9A27E', accent: '#B98F6A', scale: 2 };

export function hallLayout(sets: readonly { set: string; parts: CatalogId[] }[], hall: readonly [number, number, number, number]): DisplaySlot[] {
  const [hx, hy, hw, hh] = hall;
  const wall: { x: number; y: number; mount: DisplaySlot['mount'] }[] = [];
  for (let x = hx; x < hx + hw; x++) wall.push({ x: x + 0.5, y: hy + 0.15, mount: 'wallBack' });
  for (let y = hy + 1; y < hy + hh; y++) wall.push({ x: hx + 0.15, y: y + 0.5, mount: 'wallLeft' });
  const out: DisplaySlot[] = [];
  let row = hy + 2, x = hx + 1;
  const floor = (s: { set: string; parts: CatalogId[] }) => {
    if (x + s.parts.length > hx + hw - 0.5) { row += 2; x = hx + 1; }
    out.push({ ...s, x, y: row, w: s.parts.length, mount: 'floor' });
    x += s.parts.length + 0.5;
  };
  for (const s of sets) {
    if (s.parts.length === 1 && wall.length > 0) out.push({ ...s, ...wall.shift()!, w: 1 });
    else floor(s);
  }
  return out;
}
export const museumSlots = () => hallLayout(fossilSets(), tuning.spaces.museum.fossilHall);

// Shared by every build (like toyMaterial), so a rebuild only makes and frees geometry.
const PLACEHOLDER_MAT = new THREE.MeshStandardMaterial({ color: '#FFFBF1', transparent: true, opacity: 0.35, roughness: 1 });
// M2 playtest: a water box under a glass box read as murky grey-green, the fish washed out behind both. Now (like the home tanks,
// assets/builders/tank.ts) the water tints only its far walls and floor, and the glass is four open-topped panes; neither writes depth.
const GLASS_MAT = new THREE.MeshStandardMaterial({ color: '#E6F6FC', transparent: true, opacity: 0.2, roughness: 0.08, depthWrite: false, side: THREE.DoubleSide });
const WATER_MAT = new THREE.MeshStandardMaterial({ color: '#36AFC4', transparent: true, opacity: 0.6, roughness: 0.5, depthWrite: false, side: THREE.BackSide });
const TANK_WOOD = '#8E6446', TANK_RIM = '#C9A27E', GRAVEL = '#D9CC9A', WEED = '#6CBE6B';

const placeholder = (id: CatalogId) => {
  const m = new THREE.Mesh(new RoundedBoxGeometry(0.62, 0.46, 0.1, 2, 0.04), PLACEHOLDER_MAT);
  m.name = `placeholder:${id}`;
  m.userData.own = true;
  m.position.y = 0.23;
  return m;
};

/** Every display under `stage` baked into a few draws (spec §8.7: a full hall was ~80 meshes), with an empty marker (no draw) left
 *  where each `part:`/`placeholder:` piece stands, so tests and tools still find what is on show. */
function bakeDisplays(stage: THREE.Group, name: string): THREE.Group {
  const out = new THREE.Group();
  out.name = name;
  stage.updateWorldMatrix(true, true);
  const inv = stage.matrixWorld.clone().invert();
  stage.traverse((o) => {
    if (!o.name.startsWith('part:') && !o.name.startsWith('placeholder:')) return;
    const marker = new THREE.Object3D();
    marker.name = o.name;
    inv.clone().multiply(o.matrixWorld).decompose(marker.position, marker.quaternion, marker.scale);
    out.add(marker);
  });
  out.add(...bakeMeshes(stage, { castShadow: true }));
  stage.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && m.userData.own) m.geometry.dispose(); });   // the staging copies
  return out;
}

/** The plinth's height: low, so the skeleton standing on it is the thing you see (v1 round 4). */
const PLINTH_TOP = 0.3;
const PLINTH = '#7F9A92';                                                   // sage stone, so cream bones stand out from it and the floor

export async function buildFossilHall(state: MuseumState, assets: Pick<AssetService, 'model'>, slots: readonly DisplaySlot[]): Promise<THREE.Group> {
  const hall = new THREE.Group();
  const have = new Set(state.fossils.map((e) => e.catalogId));
  const plinthMat = toyMaterial(PLINTH);
  for (const slot of slots) {
    const g = new THREE.Group();
    g.name = `display:${slot.set}`;
    if (slot.mount !== 'floor') {                                            // a single fossil: a plaque on the wall
      const id = slot.parts[0]!;
      const part = have.has(id) ? await assets.model(id) : placeholder(id);
      if (have.has(id)) part.name = `part:${id}`;
      part.position.set(slot.x, 1.2, slot.y);
      if (slot.mount === 'wallLeft') part.rotation.y = Math.PI / 2;
      g.add(part);
      hall.add(g);
      continue;
    }
    // A set: a low plinth with one skeleton standing on it, every part its share of the bones; the missing ones are ghosts of theirs.
    const plinth = new THREE.Mesh(new RoundedBoxGeometry(slot.w, PLINTH_TOP, 0.8, 2, 0.08), plinthMat);
    plinth.userData.own = true; plinth.receiveShadow = plinth.castShadow = true;
    plinth.position.set(slot.x + slot.w / 2, PLINTH_TOP / 2, slot.y + 0.4);
    g.add(plinth);
    for (const id of slot.parts) {
      const part = await assets.model(id, { display: 'skeleton' });         // its own tile's centre on the plinth top (skeletonSlot)
      if (have.has(id)) part.name = `part:${id}`;
      else {
        part.name = `placeholder:${id}`;
        part.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.material = PLACEHOLDER_MAT; });
      }
      part.position.set(slot.x + skeletonSlot(getItem(id) as FossilEntry) + 0.5, PLINTH_TOP, slot.y + 0.4);
      g.add(part);
    }
    hall.add(g);
  }
  return bakeDisplays(hall, 'fossilHall');
}

type TankId = 'river' | 'pond' | 'sea' | 'feature';
/** The tanks in room tiles: each [x, y, w, d] of `tanks` offset by the aquarium rect's corner (both from tuning.spaces.museum, G6). */
export function aquariumTanks(aquarium: readonly number[], tanks: Readonly<Record<TankId, readonly number[]>>): { tank: TankId; x: number; y: number; w: number; d: number }[] {
  const [ax, ay] = aquarium as [number, number];
  return (['river', 'pond', 'sea', 'feature'] as const).map((tank) => {
    const [x, y, w, d] = tanks[tank] as [number, number, number, number];
    return { tank, x: ax + x, y: ay + y, w, d };
  });
}
export const TANKS = aquariumTanks(tuning.spaces.museum.aquarium, tuning.spaces.museum.tanks);

type FloorRect = { x: number; y: number; w: number; d: number };
/** The floor nobody walks through: floor fossil plinths, the tanks, the lectern and the curator himself. The scene's `walkable`
 *  and the curator's standing spot both come from this, so they cannot drift apart. */
export function museumBlocked(): FloorRect[] {
  const L = tuning.spaces.museum, [cx, cy] = L.curator;
  return [...museumSlots().filter((s) => s.mount === 'floor').map((s) => ({ x: s.x, y: s.y, w: s.w, d: 0.8 })),
    ...TANKS.map((t) => ({ x: t.x, y: t.y, w: t.w, d: t.d })),
    { x: cx - 0.6, y: cy - 1.05, w: 1.2, d: 0.5 },                                // the lectern
    { x: cx - 0.35, y: cy - 0.35, w: 0.7, d: 0.7 }];                              // the curator himself
}
/** Inside the room's walls and off every blocked rect. */
export function museumWalkable(p: Vec2, blocked: readonly FloorRect[] = museumBlocked()): boolean {
  const L = tuning.spaces.museum;
  return p.x > 0.3 && p.y > 0.3 && p.x < L.w - 0.3 && p.y < L.h - 0.2 && !blocked.some((b) => p.x > b.x && p.x < b.x + b.w && p.y > b.y && p.y < b.y + b.d);
}
/** How far short of a tile centre a walk may stop (TapToWalk's arrival radius), kept as margin under interactRange. */
const ARRIVE_SLACK = 0.25;
/** Where a player stands to talk to the curator: a tile centre, because every walk (tap or `__wt.walkTo`) floors its goal to a
 *  tile and stops at that tile's centre. The nearest walkable tile centre in front of the lectern (y ≥ the curator's row) that is
 *  within tuning.town.interactRange of him, with the arrival slack to spare, so museum.identify/donate pass the server's range check. */
export function curatorStandPoint(): Vec2 {
  const [cx, cy] = tuning.spaces.museum.curator, blocked = museumBlocked();
  let best: Vec2 | null = null, bestD = Infinity;
  for (let ty = Math.floor(cy); ty <= Math.floor(cy) + 1; ty++) {
    for (const tx of [Math.floor(cx), Math.floor(cx) - 1, Math.floor(cx) + 1]) {
      const p = { x: tx + 0.5, y: ty + 0.5 }, d = Math.hypot(p.x - cx, p.y - cy);
      if (d + ARRIVE_SLACK <= tuning.town.interactRange && d < bestD && museumWalkable(p, blocked)) { best = p; bestD = d; }
    }
  }
  if (!best) throw new Error('museum layout leaves no tile in front of the curator within interactRange');
  return best;
}

/** Four upright panes (no lid) round a w × d footprint centred on 0, `h` tall from `y0`. */
function panes(w: number, d: number, y0: number, h: number): THREE.Object3D[] {
  return ([[0, d / 2, 0, w], [0, -d / 2, Math.PI, w], [w / 2, 0, Math.PI / 2, d], [-w / 2, 0, -Math.PI / 2, d]] as const)
    .map(([x, z, ry, len]) => new THREE.Mesh(new THREE.PlaneGeometry(len, h).rotateY(ry).translate(x, y0 + h / 2, z), GLASS_MAT));
}

export async function buildAquarium(state: MuseumState, assets: Pick<AssetService, 'model'>): Promise<THREE.Group> {
  const root = new THREE.Group();
  root.name = 'aquarium';
  const tanks = new Map<string, THREE.Group>();
  const swimmers: Swimmer[] = [];
  const fixed = new THREE.Group();                                         // the tanks never move: baked into three draws below
  for (const t of TANKS) {
    const g = new THREE.Group();
    g.name = `tank:${t.tank}`;
    const h = t.tank === 'feature' ? 1.6 : 1.1, wy0 = 0.36, wh = h - 0.1;
    const tank = new THREE.Group();
    tank.position.set(t.x + t.w / 2, 0, t.y + t.d / 2);
    const base = new THREE.Mesh(new RoundedBoxGeometry(t.w, 0.3, t.d, 2, 0.06), toyMaterial(TANK_WOOD));
    base.position.y = 0.15;
    const gravel = new THREE.Mesh(new RoundedBoxGeometry(t.w - 0.14, 0.08, t.d - 0.14, 2, 0.03), toyMaterial(GRAVEL));
    gravel.position.y = 0.33;
    const water = new THREE.Mesh(new THREE.BoxGeometry(t.w - 0.12, wh, t.d - 0.12), WATER_MAT);
    water.position.y = wy0 + wh / 2;
    tank.add(base, gravel, water, ...panes(t.w - 0.08, t.d - 0.08, 0.3, h));
    for (const [x, z, rw, rd] of [[0, (t.d - 0.08) / 2, t.w, 0.06], [0, -(t.d - 0.08) / 2, t.w, 0.06], [(t.w - 0.08) / 2, 0, 0.06, t.d], [-(t.w - 0.08) / 2, 0, 0.06, t.d]] as const) {
      const bar = new THREE.Mesh(new RoundedBoxGeometry(rw, 0.06, rd, 2, 0.02), toyMaterial(TANK_RIM));
      bar.position.set(x, 0.3 + h, z); tank.add(bar);
    }
    for (const sx of [-1, 1]) {                                            // water weeds in the back corners
      const weed = new THREE.Mesh(new THREE.ConeGeometry(0.07, Math.min(0.6, wh * 0.6), 5), toyMaterial(WEED));
      weed.position.set(sx * (t.w / 2 - 0.2), 0.37 + Math.min(0.6, wh * 0.6) / 2, -(t.d / 2 - 0.2)); tank.add(weed);
    }
    fixed.add(tank);
    g.userData.box = { cx: t.x + t.w / 2, cz: t.y + t.d / 2, w: t.w, d: t.d, y: wy0 + wh / 2, h: wh };
    tanks.set(t.tank, g);
    root.add(g);
  }
  root.add(...bakeMeshes(fixed, { castShadow: true }));
  fixed.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
  const lanes = new Map<string, number>();
  for (const [i, e] of state.fish.entries()) {
    const entry = getItem(e.catalogId) as FishEntry, which = museumTank(entry);
    const tank = tanks.get(which)!;
    const box = tank.userData.box as SwimBox;
    const fish = bakeFish(await assets.model(e.catalogId));                // two draws a fish (spec §8.7), not five to thirteen
    fish.name = `fish:${e.catalogId}`;
    // Big enough to see from the doorway (M2 playtest: a dace was a quarter tile long), small enough to turn in its tank.
    const natural = fishScale(entry.look.length), len = THREE.MathUtils.clamp(natural * 1.8, 0.55, box.w * 0.42);
    fish.scale.setScalar(len / natural);
    const lane = lanes.get(which) ?? 0;
    lanes.set(which, lane + 1);
    fish.position.set(box.cx, box.y, box.cz);
    tank.add(fish);
    swimmers.push({ obj: fish, tail: fish.getObjectByName('tail') ?? null, box, len, phase: i * 1.7, speed: 0.45 + (i % 3) * 0.15,
      lane: { y: ((lane % 3) - 1) * box.h * 0.25, z: (((lane + 1) % 3) - 1) * box.d * 0.18 } });
  }
  root.userData.swimmers = swimmers;
  return root;
}

type SwimBox = { cx: number; cz: number; w: number; d: number; y: number; h: number };
type Swimmer = { obj: THREE.Object3D; tail: THREE.Object3D | null; box: SwimBox; len: number; phase: number; speed: number; lane: { y: number; z: number } };
/** Fish positions are a pure function of time; called every frame from the museum scene's `update` (rev 5 item 12).
 *  (`onBeforeRender` would never fire here: three.js calls it only on renderable objects, and a fish model is a Group.) */
export function swimFish(aquarium: THREE.Group, tSec: number): void {
  for (const f of (aquarium.userData.swimmers ?? []) as Swimmer[]) {
    const a = tSec * f.speed + f.phase, reach = Math.max(0.05, f.box.w / 2 - f.len / 2 - 0.12);
    f.obj.position.set(f.box.cx + Math.sin(a) * reach, f.box.y + f.lane.y + Math.sin(tSec * 1.3 + f.phase) * 0.04, f.box.cz + f.lane.z);
    f.obj.rotation.y = Math.cos(a) >= 0 ? 0 : Math.PI;                    // nose (+x) first: end to end along the tank, turning at each end
    if (f.tail) f.tail.rotation.y = Math.sin(tSec * 7 + f.phase) * 0.35;
  }
}
/** The museum's SpaceScene.update: the curator's mixer by dt, the fish by the town clock. `aquarium` is a getter because
 *  refreshMuseum swaps the aquarium group when a fish is donated. */
export function museumUpdate(parts: { curator: { update(dt: number): void }; aquarium: () => THREE.Group }): (dt: number, nowMs: number) => void {
  return (dt, nowMs) => { parts.curator.update(dt); swimFish(parts.aquarium(), nowMs / 1000); };
}

/** Frees the geometry this module made or baked (the hall and tanks, each fish's body and tail, the lectern; the curator frees his own),
 *  every mesh marked `userData.own`. Their materials are shared (toyMaterial, BAKED_MATERIAL and the module-level placeholder/glass/
 *  water ones) and stay. A code builder's model is built fresh per assets.model() call, so whatever of it is drawn must be own (baked);
 *  kit models are clones that share the kit's cached geometry and are left alone. */
function disposeOwn(root: THREE.Object3D): void {
  root.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh && m.userData.own) m.geometry.dispose(); });
}

let current: { scene: THREE.Scene; hall: THREE.Group; aquarium: THREE.Group; gen: number } | null = null;
export function refreshMuseum(game: Game, state: MuseumState): void {
  const live = current;
  if (!live || game.scenes.current?.scene !== live.scene) return;
  const gen = ++live.gen;                                                   // overlapping rebuilds: only the newest one lands
  void Promise.all([buildFossilHall(state, game.assets, museumSlots()), buildAquarium(state, game.assets)]).then(([hall, aquarium]) => {
    if (current !== live || live.gen !== gen) { disposeOwn(hall); disposeOwn(aquarium); return; }   // left the museum, or superseded
    live.scene.remove(live.hall, live.aquarium);
    disposeOwn(live.hall); disposeOwn(live.aquarium);
    live.scene.add(hall, aquarium);
    live.hall = hall; live.aquarium = aquarium;
  });
}

export const space: SpaceSceneDef = {
  kind: 'museum',
  async build(snap: SpaceSnapshot, game: Game): Promise<SpaceScene> {
    if (snap.kind !== 'museum') throw new Error('museum scene needs a museum snapshot');
    const L = tuning.spaces.museum;
    const { scene, room } = interiorScene({ w: L.w, h: L.h, wallpaper: WALL, floor: FLOOR, window: false });
    const [hall, aquarium, curator] = await Promise.all([buildFossilHall(snap.museum, game.assets, museumSlots()), buildAquarium(snap.museum, game.assets), createCurator(game.assets, 1.7)]);
    curator.root.position.set(L.curator[0], 0, L.curator[1]);
    const lectern = new THREE.Mesh(new RoundedBoxGeometry(1.2, 0.9, 0.5, 2, 0.06), toyMaterial('#B08A63'));
    lectern.position.set(L.curator[0], 0.45, L.curator[1] - 0.8);
    lectern.name = 'lectern'; lectern.userData.own = true; lectern.castShadow = lectern.receiveShadow = true;
    scene.add(hall, aquarium, curator.root, lectern);
    const live = { scene, hall, aquarium, gen: 0 };                          // refreshMuseum swaps hall and aquarium in place
    current = live;
    const update = museumUpdate({ curator, aquarium: () => live.aquarium });
    const blocked = museumBlocked();
    const walkable = (p: Vec2) => museumWalkable(p, blocked);
    return {
      scene, bounds: { x: 0, y: 0, w: L.w, h: L.h }, heightAt: () => 0,
      walkable, canMove: (_from, to) => walkable(to),
      update,                                                                 // driven by game/systems/spaces.ts every frame
      tick: (_dt, minute) => room.light(minute),                              // light and look: the hall follows the town clock
      dispose: () => {
        if (current === live) current = null;
        disposeOwn(live.hall); disposeOwn(live.aquarium); curator.dispose(); disposeOwn(lectern);
        room.dispose();
      },
    };
  },
  apply(snap, _scene, game) { if (snap.kind === 'museum') refreshMuseum(game, snap.museum); },
};
